import { forwardRef } from "react";
import { cn } from "@/lib/utils";

/**
 * The one button of the site. A lime primary (the only filled lime on a page), a glass secondary, a quiet ghost and a text link.
 * Every native prop passes through untouched (onClick, disabled, href, to, data-testid, data-cta, ...), so analytics hooks keep working.
 * `as` takes a tag or a component: <Button as={Link} to="/analyze">. It is separate from components/ui/button.jsx (the shadcn one) on purpose.
 */
const SIZE = { md: "h-12 px-5 text-[15px]", lg: "h-14 px-7 text-base", xl: "h-16 px-9 text-lg" };
const VARIANT = {
  primary: "bg-gradient-to-b from-lime-300 to-lime-400 text-black font-bold shadow-glow hover:shadow-[0_10px_44px_rgba(163,230,53,0.4)] disabled:opacity-40 disabled:shadow-none",
  secondary: "glass border-white/15 text-zinc-100 font-semibold hover:bg-white/10",
  ghost: "text-zinc-200 font-semibold hover:bg-white/5",
  link: "text-lime-300 font-semibold underline-offset-4 hover:underline !h-auto !px-0",
};

/** The button's classes, for things that render their own <button> (PracticePassButton takes a className). */
export const buttonClasses = ({ variant = "primary", size = "md", pill = false, className } = {}) => cn(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap transition-[transform,box-shadow,background-color,opacity] duration-press ease-standard active:scale-[.97] disabled:cursor-not-allowed",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950",
  pill ? "rounded-full" : "rounded-btn",
  SIZE[size] || SIZE.md,
  VARIANT[variant] || VARIANT.primary,
  className
);

const Button = forwardRef(function Button(
  { as: Tag = "button", variant = "primary", size = "md", pill = false, icon: Icon, iconRight: IconRight, className, children, ...rest },
  ref
) {
  const props = Tag === "button" ? { type: "button", ...rest } : rest;
  return (
    <Tag
      ref={ref}
      data-kit-motion
      {...props}
      className={buttonClasses({ variant, size, pill, className })}
    >
      {Icon ? <Icon className="w-[1.15em] h-[1.15em] shrink-0" aria-hidden="true" /> : null}
      {children}
      {IconRight ? <IconRight className="w-[1.05em] h-[1.05em] shrink-0" aria-hidden="true" /> : null}
    </Tag>
  );
});

export default Button;
